import type { CompanyProfile, PostalAddress } from '@/api/types'

function trimOrUndef(s?: string): string | undefined {
  const t = s?.trim()
  return t ? t : undefined
}

function normalizeAddress(a?: PostalAddress): PostalAddress | undefined {
  const street = trimOrUndef(a?.street)
  const postalCode = trimOrUndef(a?.postalCode)
  const city = trimOrUndef(a?.city)
  const country = trimOrUndef(a?.country)
  if (!street && !postalCode && !city && !country) return undefined
  return { street, postalCode, city, country }
}

/**
 * Build the canonical nested CompanyProfile submitted to the API: trims all fields, drops
 * empty values, and omits optional sections (location/contact/media/receipt) that are empty.
 * The legal block is always present (its required-ness is enforced by the form's zod schema).
 */
export function normalizeCompanyProfile(
  input: CompanyProfile | undefined,
): CompanyProfile {
  const legal = {
    name: trimOrUndef(input?.legal?.name),
    uid: trimOrUndef(input?.legal?.uid),
    registerId: trimOrUndef(input?.legal?.registerId),
    owner: trimOrUndef(input?.legal?.owner),
    address: normalizeAddress(input?.legal?.address),
  }

  const out: CompanyProfile = { legal }

  const locationAddress = normalizeAddress(input?.location?.address)
  if (locationAddress) out.location = { address: locationAddress }

  const phone = trimOrUndef(input?.contact?.phone)
  const email = trimOrUndef(input?.contact?.email)
  const websiteUrl = trimOrUndef(input?.contact?.websiteUrl)
  if (phone || email || websiteUrl) out.contact = { phone, email, websiteUrl }

  const thumbnailUrl = trimOrUndef(input?.media?.thumbnailUrl)
  const headerImageUrl = trimOrUndef(input?.media?.headerImageUrl)
  if (thumbnailUrl || headerImageUrl)
    out.media = { thumbnailUrl, headerImageUrl }

  const hoursText = trimOrUndef(input?.receipt?.hoursText)
  if (hoursText) out.receipt = { hoursText }

  const urlSlug = trimOrUndef(input?.urlSlug)
  if (urlSlug) out.urlSlug = urlSlug.toLowerCase()

  return out
}
